import React, { useState, useMemo } from 'react';
import { 
    Box, Typography, Container, Paper, 
    TextField, InputAdornment, Button, Chip, 
    Grid, Tooltip, IconButton, useTheme 
} from '@mui/material';
import { 
    Security as SecurityIcon, 
    Search as SearchIcon, 
    FilterList as FilterListIcon, 
    VpnKey as VpnKeyIcon,
    Person as PersonIcon,
    CloudDownload as CloudDownloadIcon,
    // --- MOVED ICON IMPORTS (FIXED ESLINT ERROR) ---
    CheckCircle as CheckCircleIcon, 
    Error as ErrorIcon, 
    Warning as WarningIcon, 
    Info as InfoIcon 
} from '@mui/icons-material';
// The separate imports for CheckCircle, Error, Warning, and Info are now included above
// if they are being imported from the main '@mui/icons-material' bundle like the others.
// If not, they should be imported separately like this:
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import ErrorIcon from '@mui/icons-material/Error';
// import WarningIcon from '@mui/icons-material/Warning';
// import InfoIcon from '@mui/icons-material/Info';

import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import moment from 'moment'; // Recommended utility for handling dates

// --- MOCK LOG DATA (Functional Content) ---
const mockLogs = [
    { id: 1, timestamp: '2025-10-05T10:30:00Z', user: 'admin_jdoe', action: 'LOGIN_SUCCESS', resource: 'AUTH_SERVICE', ip: '203.0.113.42', status: 'Success', details: 'Successful login from internal network.', location: 'Internal' },
    { id: 2, timestamp: '2025-10-05T10:31:45Z', user: 'user_asmith', action: 'RESOURCE_ACCESS', resource: 'REPORT_FINANCIAL', ip: '198.51.100.1', status: 'Success', details: 'Accessed Q3 Financial Report.', location: 'External' },
    { id: 3, timestamp: '2025-10-05T10:35:12Z', user: 'guest_user', action: 'LOGIN_FAILURE', resource: 'AUTH_SERVICE', ip: '192.0.2.25', status: 'Failure', details: 'Invalid credentials attempt.', location: 'Europe' },
    { id: 4, timestamp: '2025-10-05T11:00:00Z', user: 'system_cron', action: 'DATA_UPDATE', resource: 'DB_PAYROLL', ip: '127.0.0.1', status: 'Success', details: 'Monthly payroll processed.', location: 'Internal' },
    { id: 5, timestamp: '2025-10-05T11:05:30Z', user: 'admin_jdoe', action: 'PERMISSIONS_CHANGE', resource: 'USER_MGR_API', ip: '203.0.113.42', status: 'Warning', details: 'Changed user_asmith role from Viewer to Editor.', location: 'Internal' },
    { id: 6, timestamp: '2025-10-05T11:15:22Z', user: 'user_bloggs', action: 'LOGIN_FAILURE', resource: 'AUTH_SERVICE', ip: '192.0.2.10', status: 'Failure', details: 'Account locked due to multiple failed attempts.', location: 'Asia' },
];

// Helper function for status chip color
const getStatusChip = (status, theme) => {
    let color;
    let icon;

    switch (status) {
        case 'Success':
            color = theme.palette.success.main;
            icon = <CheckCircleIcon style={{ fontSize: 16 }} />;
            break;
        case 'Failure':
            color = theme.palette.error.main;
            icon = <ErrorIcon style={{ fontSize: 16 }} />;
            break;
        case 'Warning':
            color = theme.palette.warning.main;
            icon = <WarningIcon style={{ fontSize: 16 }} />;
            break;
        default:
            color = theme.palette.info.main;
            icon = <InfoIcon style={{ fontSize: 16 }} />;
    }

    return (
        <Chip 
            label={status} 
            size="small" 
            icon={icon}
            sx={{ 
                backgroundColor: color, 
                color: 'white', 
                fontWeight: 'bold', 
                '& .MuiChip-icon': { color: 'white' },
                minWidth: 80 
            }}
        />
    );
};

// --- DATAGRID COLUMN DEFINITIONS ---
const createColumns = (theme) => [
    { field: 'timestamp', headerName: 'Time (UTC)', width: 180, 
        renderCell: (params) => moment(params.value).format('YYYY-MM-DD HH:mm:ss'),
        sortable: true,
        headerClassName: 'super-app-theme--header'
    },
    { field: 'user', headerName: 'User/System', width: 150, 
        renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon fontSize="small" color="primary" sx={{ mr: 0.5 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{params.value}</Typography>
            </Box>
        )
    },
    { field: 'action', headerName: 'Action Type', width: 180, 
        renderCell: (params) => (
            <Chip 
                label={params.value} 
                size="small" 
                color="info" 
                variant="outlined"
                sx={{ borderColor: theme.palette.info.light, color: theme.palette.info.dark, fontWeight: 500 }}
            />
        ) 
    },
    { field: 'resource', headerName: 'Resource/Target', width: 200 },
    { field: 'ip', headerName: 'IP Address', width: 130, 
        renderCell: (params) => (
            <Tooltip title={`Location: ${params.row.location}`} placement="top">
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{params.value}</Typography>
            </Tooltip>
        )
    },
    { field: 'status', headerName: 'Status', width: 120, 
        renderCell: (params) => getStatusChip(params.value, theme) 
    },
    { field: 'details', headerName: 'Details', flex: 1, minWidth: 250 },
];

// --- MAIN COMPONENT ---
const AccessControlLogs = () => {
    const theme = useTheme();
    const [searchText, setSearchText] = useState('');

    const columns = useMemo(() => createColumns(theme), [theme]);

    // Functional Filtering Logic
    const filteredLogs = useMemo(() => {
        if (!searchText) return mockLogs;
        
        const lowerSearch = searchText.toLowerCase();
        
        return mockLogs.filter(log => 
            log.user.toLowerCase().includes(lowerSearch) ||
            log.action.toLowerCase().includes(lowerSearch) ||
            log.resource.toLowerCase().includes(lowerSearch) ||
            log.ip.includes(lowerSearch) ||
            log.details.toLowerCase().includes(lowerSearch)
        );
    }, [searchText]);

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, 
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 4
                }}
            >
                <VpnKeyIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.secondary.main }} />
                Access Control & Audit Logs
            </Typography>

            <Paper elevation={6} sx={{ p: 3, borderRadius: 2 }}>
                
                {/* Search and Action Bar (Functional & Responsive) */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Search Logs"
                            placeholder="Search by user, resource, or IP..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            color="info"
                            // Placeholder for advanced filtering functionality
                        >
                            Advanced Filters
                        </Button>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<CloudDownloadIcon />}
                            color="secondary"
                            // Placeholder for export functionality
                            onClick={() => alert("Downloading logs... (Mock Action)")}
                        >
                            Export CSV
                        </Button>
                    </Grid>
                </Grid>

                {/* DataGrid Component (Functional & High Performance) */}
                <Box sx={{ height: 600, width: '100%', mt: 2 }}>
                    <DataGrid
                        rows={filteredLogs}
                        columns={columns}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                            sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] }, // Default sort by date
                        }}
                        checkboxSelection
                        disableRowSelectionOnClick
                        slots={{
                            toolbar: GridToolbar, // Built-in toolbar for filtering/exporting
                        }}
                        sx={{
                            // Pro-level styling for the data grid
                            border: 1,
                            borderColor: theme.palette.divider,
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: theme.palette.grey[100],
                                fontWeight: 700,
                            },
                            '& .MuiDataGrid-cell': {
                                py: '8px !important',
                            }
                        }}
                    />
                </Box>
            </Paper>
        </Container>
    );
};

export default AccessControlLogs;